import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  downloadDocument,
  getDocuments,
  uploadDocument,
} from "../Helper/DocumentsHelper";
import UploadFileBtn from "../Helper/UploadFileBtn/UploadFileBtn";
import { DocumentsTypes } from "../Enum/DocumentsType";
import userRoles from "../../configs/userRolesConfigs";
import { Box, Button } from "@mui/material";
import { downloadReimbursementsToCharge, getBillingsInfo, getInvoiceDetailsPage } from "./InvoicesUtils";
import { useSearchParams } from "react-router-dom";
import "./Invoices.scss";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import DataGridTamplet from "../Helper/TableTemplet/DataGridTamplet";
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import NewPaymentMethod from "../PaymentMethodForm/NewPaymentMethod";
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { async } from "q";
import InvoicesService from "../../Services/http/InvoicesService";

const PaymentMethodTypes = {
  us_bank_account: 'US Bank Account',
  card: 'Credit Card'
};


const Invoices = () => {
  const dispatch = useDispatch();
  const [invoices, setInvoices] = useState([]);
  const [billingsInfo, setBillingsInfo] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const isDownload = searchParams.get("download");
  const [addPaymentMethodOpen, setAddPaymentMethodOpen] = useState(false);

  const defaultAccount = useSelector(
    (state: any) => state.account.default_account
  );
  const userRole = useSelector((state: any) => state.general.userRol);

  const columns: GridColDef[] = [
    {
      field: "invoice_pdf",
      headerName: "Invoice PDF",
      sortable: true,
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <PictureAsPdfOutlinedIcon
            className="pointer"
            onClick={() => handleInvoicePDF(params.id as number, params.row)}
            sx={{ ml: 3 }}
          >
          </PictureAsPdfOutlinedIcon>
        </Box>
      )
    },
    { field: "billing_details", headerName: "Billing Details", sortable: true, width: 280 },
    {
      field: "is_paid", headerName: "Status", sortable: true, width: 100,
      valueGetter: (params) => {
        if (!params.value) {
          return 'NOT PAID';
        } else {
          return 'PAID';
        }
      },

    },
    {
      field: "amount",
      headerName: "Invoice Amount ($)",
      sortable: true,
      width: 180,
      valueFormatter: ({ value }) => value && value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    {
      field: "invoice_date",
      headerName: "Invoice Date",
      sortable: true,
      width: 180,
    },
    {
      field: "pay_invoice",
      headerName: "Pay Invoice",
      sortable: true,
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <PaymentsOutlinedIcon
            className="pointer"
            onClick={() => openPaymentPage(params.row.payment_page_link)}
            sx={{ ml: 3 }}
          >
          </PaymentsOutlinedIcon>
        </Box>
      )
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setBillingsInfo(await getBillingsInfo(dispatch, defaultAccount.id));
      setInvoices(await getDocuments(dispatch, "invoice", defaultAccount.id));
    };
    if (defaultAccount && defaultAccount.id) fetchData();
    if (isDownload === 'true' && defaultAccount && defaultAccount.id) {
      downloadReimbursementsFile();
    }
  }, [defaultAccount]);
  
  const handleInvoicePDF = (invoiceId, params) => {
    if (params.is_manual) {
      openInvoiceDetailsPage(invoiceId);
    } else {
      downloadReimbursementsFile(invoiceId);
    }
  };

  const openInvoiceDetailsPage = async (invoiceId) => {
    const response = await getInvoiceDetailsPage(dispatch, defaultAccount.id, invoiceId);
    if (response && response['message'] === 'success') {
      window.open(response['invoice_details_page'], '_blank');
    }
  };

  const importDocument = async (file) => {
    setInvoices(
      await uploadDocument(
        dispatch,
        file,
        DocumentsTypes.INVOICE,
        defaultAccount.id
      )
    );
  };

  const download = async (item) => {
    try {
      await downloadDocument(dispatch, defaultAccount.id, item.id, item.name);
    } catch (error) {
      alert(error);
    }
  };

  const downloadReimbursementsFile = (invoice_id = null) => {
    downloadReimbursementsToCharge(dispatch, defaultAccount.id, invoice_id);
  };

  const openPaymentPage = (payment_page_link) => {
    if (payment_page_link) {
      window.open(payment_page_link, '_blank');
    }
  }

  const getFirstDayNextMonth = () => {
    // Get the current date
    const currentDate = new Date();

    // Calculate the next month
    const nextMonth = new Date(currentDate);
    nextMonth.setDate(1); // Set the day to 1

    // Add one month
    nextMonth.setMonth(currentDate.getMonth() + 1);

    // Format the date if needed
    return nextMonth.toLocaleDateString(); // You can change the format as per your requirements

  };

  return (
    <Box>
      <h2>Billing</h2>
      <Box className="account-box">
        <span className="account-txt">Account info</span>
        <hr />
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box className="account-item-box">
            <Box>
              <span className="bold f-14">Account Name</span><br />
              <span className="account-txt">{billingsInfo['email']}</span>
            </Box>
          </Box>
          <Box className="account-item-box">
            <Box>
              <span className="bold f-14">Current Subscription</span><br />
              {billingsInfo['is_active'] ?
                <span className="account-txt bgc-green p-2p">ACTIVE</span> : billingsInfo['is_active'] === false ? <span className="bgc-red p-2p">INACTIVE</span> : ''}
            </Box>

          </Box>
          <Box className="account-item-box">
            <Box>
              <span className="bold f-14">Payment Terms</span><br />
              <span className="account-txt">Monthly</span>
            </Box>
          </Box>
          <Box className="account-item-box">
            <Box>
              <span className="bold f-14">Next Payment</span><br />
              <span className="account-txt">
                ${billingsInfo['amount_to_charge'] ? billingsInfo['amount_to_charge'].toFixed(2) : '0.0'}</span>
            </Box>
          </Box>
          <Box className="account-item-box">
            <Box>
              <span className="bold f-14">Default Payment Method</span><br />
              {billingsInfo['default_payment_method'] &&
                <span className="account-txt">
                  {PaymentMethodTypes[billingsInfo['default_payment_method']['type']]}, ****
                  {billingsInfo['default_payment_method']['last4']}</span>
              }
            </Box>
          </Box>
          <Box className="account-item-box">
            <Button onClick={() => setAddPaymentMethodOpen(true)}>Add Payment Method</Button>
          </Box>
        </Box>
      </Box>
      <Box className="billings-box">
        <span className="billings-txt">Billing History</span>
        <hr />
        <DataGridTamplet columns={columns} rows={billingsInfo['billings']} height={250}></DataGridTamplet>

      </Box>
      <NewPaymentMethod open={addPaymentMethodOpen}
        setOpen={setAddPaymentMethodOpen}
      ></NewPaymentMethod>
    </Box>
  );

  // return (
  //   <div>
  //     <h2>Billings</h2>
  //     {invoices.map((item) => {
  //       return (
  //         <div
  //           className="file"
  //           key={item.id}
  //           onClick={() => download(item)}
  //         >
  //           {item.name}
  //         </div>
  //       );
  //     })}
  //      <Box sx={{marginTop: "20px"}}>
  //       <Button
  //         className="dark-blue-btn"
  //         onClick={downloadReimbursementsFile}
  //       >
  //         download Reimbursements File
  //       </Button>

  //     </Box>
  //     {(userRole === userRoles.admin || userRole === userRoles.analyst) && (
  //       <UploadFileBtn
  //         onSubmit={importDocument}
  //         multiple={false}
  //         classes="m-auto"
  //         title="Upload Documents"
  //         buttonText="Upload"
  //         exdendStyle={true}
  //       ></UploadFileBtn>
  //     )}
  //   </div>
  // );
};

export default Invoices;
